import { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { useAuth } from "../auth/useAuth.js";
import { Botao, Campo, Entrada, Falha, Selecao } from "../../components/ui.js";

/**
 * Onde o lote aconteceu: o local e a vivência.
 *
 * Obrigatório porque é o que liga as fotos de volta ao evento. Sem isso um lote
 * de quarenta vira quarenta capturas soltas na Biblioteca, e quem for procurá-las
 * na semana seguinte não tem por onde puxar.
 *
 * Cadastrar daqui mesmo existe porque o operador descobre o local faltando no
 * pior momento — com as fotos já enviadas. O botão só aparece para quem pode:
 * `institutions.create` e `vivencias.create` são `clienteProcedure`, e ampliar
 * isso para o operador ampliaria no sistema inteiro, não só aqui.
 */
export function EscolherEvento({
  institutionId,
  vivenciaId,
  aoMudarLocal,
  aoMudarVivencia,
}: {
  institutionId: string;
  vivenciaId: string;
  aoMudarLocal: (id: string) => void;
  aoMudarVivencia: (id: string) => void;
}) {
  const { papel } = useAuth();
  const podeCadastrar = papel === "admin" || papel === "cliente";

  const utils = trpc.useUtils();
  const locais = trpc.institutions.list.useQuery({});
  // A lista acompanha o local escolhido: sem isso o operador monta combinações
  // que não existem e recebe uma recusa do servidor sem entender por quê.
  const vivencias = trpc.vivencias.list.useQuery(
    { institutionId },
    { enabled: institutionId !== "" },
  );

  const [criando, setCriando] = useState<"local" | "vivencia" | null>(null);
  const [nome, setNome] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const novoLocal = trpc.institutions.create.useMutation();
  const novaVivencia = trpc.vivencias.create.useMutation();

  function abrir(qual: "local" | "vivencia") {
    setCriando(criando === qual ? null : qual);
    setNome("");
    setErro(null);
  }

  async function cadastrar() {
    setErro(null);
    try {
      if (criando === "local") {
        const criado = await novoLocal.mutateAsync({ name: nome.trim() });
        await utils.institutions.list.invalidate();
        // Já selecionado: quem acabou de cadastrar não deveria ter de procurar.
        aoMudarLocal(criado.id);
        aoMudarVivencia("");
      } else {
        const criada = await novaVivencia.mutateAsync({
          name: nome.trim(),
          institutionId,
        });
        await utils.vivencias.list.invalidate();
        aoMudarVivencia(criada.id);
      }
      setCriando(null);
      setNome("");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível cadastrar");
    }
  }

  const salvando = novoLocal.isPending || novaVivencia.isPending;

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="font-display text-[1.125rem] leading-tight">O evento</h2>
        <p className="mt-1 text-[0.8125rem] leading-relaxed text-texto-suave">
          É por aqui que estas fotos serão encontradas na Biblioteca depois.
        </p>
      </div>

      <Campo rotulo="Local">
        <Selecao
          value={institutionId}
          onChange={(e) => {
            aoMudarLocal(e.target.value);
            // Trocar de local invalida a vivência escolhida: ela era de outro.
            aoMudarVivencia("");
          }}
        >
          <option value="">Selecione o local</option>
          {(locais.data ?? []).map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </Selecao>
      </Campo>

      <Campo
        rotulo="Vivência"
        dica={institutionId === "" ? "Escolha o local primeiro." : undefined}
      >
        <Selecao
          value={vivenciaId}
          onChange={(e) => aoMudarVivencia(e.target.value)}
          disabled={institutionId === ""}
        >
          <option value="">Selecione a vivência</option>
          {(vivencias.data ?? []).map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </Selecao>
      </Campo>

      {podeCadastrar && (
        <div className="flex flex-wrap gap-x-5">
          <Botao formato="nu" tom="fantasma" onClick={() => abrir("local")}>
            {criando === "local" ? "Cancelar" : "+ Novo local"}
          </Botao>
          <Botao
            formato="nu"
            tom="fantasma"
            disabled={institutionId === ""}
            onClick={() => abrir("vivencia")}
          >
            {criando === "vivencia" ? "Cancelar" : "+ Nova vivência"}
          </Botao>
        </div>
      )}

      {criando !== null && (
        <div className="flex flex-col gap-3 rounded-peca border border-borda bg-superficie-alta p-4">
          {erro && <Falha mensagem={erro} tentarNovamente={() => setErro(null)} />}

          <Campo rotulo={criando === "local" ? "Nome do local" : "Nome da vivência"}>
            <Entrada
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder={criando === "local" ? "Casa de Acolhimento São José" : "Mateus 24"}
              maxLength={200}
              autoFocus
            />
          </Campo>

          <Botao
            tom="quieto"
            disabled={nome.trim().length < 2}
            carregando={salvando}
            onClick={() => void cadastrar()}
          >
            Cadastrar {criando === "local" ? "local" : "vivência"}
          </Botao>
        </div>
      )}
    </section>
  );
}
